'use client'

import { useEffect, useRef, useState } from 'react'
import { useMotionValue, useMotionValueEvent, useReducedMotion, useScroll, useSpring, useTransform, type MotionValue } from 'motion/react'
import { CRITICAL_FRAMES, FRAME_COUNT, MOBILE_STORY_QUERY, REFINEMENT_LEVELS, detectStoryFormat, isCriticalFrame, pickShownFrame, prefersStillStory, storyFrameUrl, type StoryFormat, type StoryVariant } from '../utils/story-frames'
import { useSequenceEndStop } from './use-sequence-end-stop'
import { useStoryOpening } from './use-story-opening'

// Production serves the story over HTTP/2+, where more parallel requests hide the per-request latency.
const MAX_IN_FLIGHT = 8
const MAX_RETRIES = 2
const NEAR_WINDOW = 6

export function useAccountingSequence() {
  const sectionRef = useRef<HTMLElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const systemReduced = useReducedMotion()
  const [preferencesReady, setPreferencesReady] = useState(false)
  const [stillPreferred, setStillPreferred] = useState(false)
  const [manualReduced, setManualReduced] = useState<boolean | null>(null)
  const [skipped, setSkipped] = useState(false)
  const reduced = skipped || (preferencesReady && (Boolean(systemReduced) || (manualReduced ?? stillPreferred)))
  const [variant, setVariant] = useState<StoryVariant | null>(null)
  const [format, setFormat] = useState<StoryFormat | null>(null)
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end end'] })
  const progress = useSpring(scrollYProgress, { stiffness: 210, damping: 42, restDelta: .0001 })
  const shownProgress = useMotionValue(0)
  const focusOpacity = useTransform(shownProgress, [0, .34, .52, 1], [0, 0, 1, 1])
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const [criticalLoaded, setCriticalLoaded] = useState(0)
  const loading = !reduced && !failed && (!ready || criticalLoaded < CRITICAL_FRAMES.length)
  const [scene, setScene] = useState(0)
  useSequenceEndStop(sectionRef, !reduced && !failed && !loading)
  const { playing: opening, prominentHint } = useStoryOpening(sectionRef, !reduced && !failed && !loading)
  useEffect(() => {
    // The native skip link works before hydration. Adopt that choice once React
    // starts, and also stop in-flight sequence requests on subsequent clicks.
    const adoptSkip = () => { if (location.hash === '#features') setSkipped(true) }
    adoptSkip()
    window.addEventListener('hashchange', adoptSkip)
    return () => window.removeEventListener('hashchange', adoptSkip)
  }, [])
  useEffect(() => {
    const media = matchMedia(MOBILE_STORY_QUERY)
    const update = () => setVariant(media.matches ? 'mobile' : 'desktop')
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])
  useEffect(() => {
    // Sequence requests start only after checking the visitor's data preference.
    // The poster remains discoverable in the initial HTML.
    setStillPreferred(prefersStillStory())
    setPreferencesReady(true)
    let active = true
    void detectStoryFormat().then(result => { if (active) setFormat(result) })
    return () => { active = false }
  }, [])
  useEffect(() => {
    if (!skipped) return
    // Wait for the loading overlay and scroll lock to leave the DOM.
    const content = document.getElementById('features')
    content?.scrollIntoView({ behavior: 'instant', block: 'start' })
    content?.focus({ preventScroll: true })
  }, [skipped])
  useMotionValueEvent(shownProgress, 'change', p => {
    const next = p < .20 ? 0 : p < .48 ? 1 : p < .73 ? 2 : 3
    setScene(old => old === next ? old : next)
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !preferencesReady || reduced || !variant || !format) return
    setReady(false)
    setFailed(false)
    setCriticalLoaded(0)
    const context = canvas.getContext('2d', { alpha: false })
    if (!context) { setFailed(true); return }
    let cancelled = false, inFlight = 0, target = 0, position = 0, last = -1, lastPosition = -1, raf = 0, resizePending = false, criticalDone = 0
    const controller = new AbortController()
    const fail = () => {
      cancelled = true
      controller.abort()
      clearTimeout(loadingTimeout)
      setFailed(true)
    }
    // Slow or stalled requests must not leave the visitor trapped behind the loader.
    const loadingTimeout = window.setTimeout(fail, 20000)
    const blobs = new Map<number, Blob>()
    const frames = new Map<number, ImageBitmap>()
    const pending = new Set<number>()
    const decoding = new Set<number>()
    const failedFrames = new Set<number>()
    const attempts = new Map<number, number>()
    const retrying = new Set<number>()
    const retryTimers = new Set<number>()
    const cacheLimit = variant === 'mobile' ? 12 : 22
    const frameUrl = (index: number) => storyFrameUrl(variant, format, index)
    // Later frames have decoded neighbours to stand in for them; the loader's frames do not.
    const giveUp = (index: number) => {
      failedFrames.add(index)
      if (isCriticalFrame(index)) fail()
    }

    const paint = (force = false) => {
      if (cancelled || !frames.size) return
      const lower = Math.floor(position)
      const upper = Math.min(FRAME_COUNT - 1, lower + 1)
      const blendEnding = position >= 158 && frames.has(lower) && frames.has(upper)
      // Blend when possible, but keep advancing through available frames if a
      // neighbour is late or permanently unavailable, including the final scene.
      const index = blendEnding ? lower : pickShownFrame(frames, last, target)
      if (index < 0 || !frames.has(index)) return
      const displayedPosition = blendEnding ? position : index
      if (lastPosition === displayedPosition && !force) return
      const image = frames.get(index)!
      const box = canvas.getBoundingClientRect()
      const dpr = Math.min(devicePixelRatio || 1, 1.5)
      const width = Math.round(box.width * dpr), height = Math.round(box.height * dpr)
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height }
      const scale = Math.max(width / image.width, height / image.height)
      const w = image.width * scale, h = image.height * scale
      context.imageSmoothingEnabled = true
      context.imageSmoothingQuality = 'high'
      context.globalAlpha = 1
      context.drawImage(image, (width - w) / 2, (height - h) / 2, w, h)
      // The plaque grows over only 22 source frames. Blend adjacent frames at
      // fractional scroll positions so its size does not advance in visible steps.
      if (blendEnding && upper !== lower) {
        context.globalAlpha = position - lower
        context.drawImage(frames.get(upper)!, (width - w) / 2, (height - h) / 2, w, h)
        context.globalAlpha = 1
      }
      last = index
      lastPosition = displayedPosition
      canvas.dataset.frame = String(index)
      canvas.dataset.position = displayedPosition.toFixed(3)
      canvas.dataset.variant = variant
      shownProgress.set(displayedPosition / (FRAME_COUNT - 1))
      setReady(true)
    }
    const schedulePaint = (resize = false) => {
      resizePending ||= resize
      if (!raf) raf = requestAnimationFrame(() => {
        raf = 0
        const force = resizePending
        resizePending = false
        paint(force)
      })
    }
    const decode = async (index: number) => {
      if (cancelled || decoding.has(index) || frames.has(index) || !blobs.has(index)) return
      decoding.add(index)
      try {
        const bitmap = await createImageBitmap(blobs.get(index)!)
        if (cancelled) { bitmap.close(); return }
        frames.set(index, bitmap)
        if (frames.size > cacheLimit) {
          const evict = [...frames.keys()].sort((a, b) => Math.abs(b - target) - Math.abs(a - target))[0]
          frames.get(evict)?.close(); frames.delete(evict)
        }
        schedulePaint()
      } catch {
        // A frame that downloads but does not decode counts as a failed download.
        if (!cancelled) { blobs.delete(index); giveUp(index) }
      }
      finally { decoding.delete(index) }
    }
    const warm = () => {
      for (const offset of [0, 1, -1, 2, -2, 3, -3, 4, -4]) {
        const i = target + offset
        if (i >= 0 && i < FRAME_COUNT) void decode(i)
      }
      // While the target is still downloading, prepare the closest frame on the way to it.
      if (blobs.has(target) || last < 0 || last === target) return
      const step = last < target ? -1 : 1
      for (let i = target + step; i !== last; i += step) if (blobs.has(i)) { void decode(i); break }
    }
    const available = (index: number) => index >= 0 && index < FRAME_COUNT && !blobs.has(index) && !pending.has(index) && !failedFrames.has(index) && !retrying.has(index)
    // The frame under the scroll position (and its successor for the ending blend), the
    // loader's frames, the scroll position's neighbourhood, then coarse-to-fine refinement.
    const nextFrame = () => {
      for (const index of [target, target + 1, ...CRITICAL_FRAMES]) if (available(index)) return index
      for (let distance = 1; distance <= NEAR_WINDOW; distance++) {
        if (available(target + distance)) return target + distance
        if (available(target - distance)) return target - distance
      }
      for (const level of REFINEMENT_LEVELS) {
        let best: number | undefined
        for (const index of level) if (available(index) && (best === undefined || Math.abs(index - target) < Math.abs(best - target))) best = index
        if (best !== undefined) return best
      }
      return undefined
    }
    const retryLater = (index: number) => {
      const attempt = (attempts.get(index) ?? 0) + 1
      attempts.set(index, attempt)
      if (attempt > MAX_RETRIES) { giveUp(index); return }
      retrying.add(index)
      const timer = window.setTimeout(() => { retryTimers.delete(timer); retrying.delete(index); pump() }, 500 * attempt)
      retryTimers.add(timer)
    }
    const pump = () => {
      if (cancelled) return
      while (inFlight < MAX_IN_FLIGHT) {
        const index = nextFrame()
        if (index === undefined) break
        pending.add(index); inFlight++
        const urgent = isCriticalFrame(index) || Math.abs(index - target) <= NEAR_WINDOW
        fetch(frameUrl(index), { signal: controller.signal, priority: urgent ? 'high' : 'low' }).then(response => {
          if (!response.ok) throw new Error('Frame unavailable')
          return response.blob()
        }).then(blob => {
          if (cancelled) return
          blobs.set(index, blob)
          if (isCriticalFrame(index)) {
            setCriticalLoaded(++criticalDone)
            // Only the loader's frames are under the deadline; the rest stream in behind the story.
            if (criticalDone === CRITICAL_FRAMES.length) clearTimeout(loadingTimeout)
          }
          if (frames.size === 0) void decode(index)
          warm()
        }).catch(() => {
          if (!cancelled) retryLater(index)
        }).finally(() => { pending.delete(index); inFlight--; pump() })
      }
    }
    const update = (p: number) => {
      position = Math.max(0, Math.min(FRAME_COUNT - 1, p * (FRAME_COUNT - 1)))
      target = Math.round(position)
      warm(); pump()
      schedulePaint()
    }
    const unsubscribe = progress.on('change', update)
    const observer = new ResizeObserver(() => schedulePaint(true))
    observer.observe(canvas)
    update(progress.get())
    return () => {
      cancelled = true; controller.abort(); clearTimeout(loadingTimeout); unsubscribe(); observer.disconnect(); cancelAnimationFrame(raf)
      for (const timer of retryTimers) clearTimeout(timer)
      for (const bitmap of frames.values()) bitmap.close()
      frames.clear(); blobs.clear()
    }
  }, [format, preferencesReady, progress, reduced, shownProgress, variant])
  const toggleMotion = () => {
    if (reduced && location.hash === '#features') {
      // replaceState would clear the URL but keep #features as the CSS :target, which keeps
      // the loader hidden. A real fragment navigation restarts the story from its beginning.
      location.replace('#history')
    }
    setSkipped(false)
    setManualReduced(!reduced)
  }
  return { sectionRef, canvasRef, progress: shownProgress, focusOpacity, reduced, ready, failed, loading, opening, prominentHint, loadingPercent: Math.min(99, Math.floor(criticalLoaded / CRITICAL_FRAMES.length * 100)), scene, toggleMotion, systemReduced: preferencesReady && Boolean(systemReduced) }
}

export function useSequenceCaption(progress: MotionValue<number>, index: number) {
  const ranges = [[0, .11, .18, .23], [.17, .24, .43, .51], [.45, .53, .68, .74], [.69, .80, 1, 1.01]][index]
  const opacity = useTransform(progress, ranges, index === 0 ? [1, 1, 1, 0] : index < 3 ? [0, 1, 1, 0] : [0, 1, 1, 1])
  const y = useTransform(progress, ranges, index === 0 ? [0, 0, -7, -32] : index < 3 ? [32, 0, 0, -28] : [32, 0, 0, 0])
  const clipPath = useTransform(progress, ranges, index === 0 ? ['inset(0 0 0% 0)', 'inset(0 0 0% 0)', 'inset(0 0 0% 0)', 'inset(0 0 100% 0)'] : ['inset(100% 0 0 0)', 'inset(0% 0 0 0)', 'inset(0% 0 0 0)', index < 3 ? 'inset(0 0 100% 0)' : 'inset(0 0 0% 0)'])
  return { opacity, y, clipPath }
}
